import * as THREE from 'three';
import type { OverheadTextLevel } from './graphics-options.js';

/** Caption sizes by look, text and interface zoom. */
const measured = new Map<string, [number, number]>();

export type OverheadEffect = 'rise' | 'pop' | 'pulse' | 'shake';
export interface OverheadMessage {
  text: string;
  color: number;
  style?: 'number' | 'notice';
  /** A caption with its own animated look (`world-overlays.css`): a Sneak Attack, or a critical hit's number. */
  flourish?: 'sneak' | 'critical';
  duration?: number;
  effects?: readonly OverheadEffect[];
  /** Follow a rendered unit; point is the fallback before a frame is available. */
  unitId?: string;
  point: { x: number; y: number; z: number };
  clearance?: number;
}

/** Shared, real-time presentation for transient world messages. It never decides gameplay. */
export class OverheadMessages {
  private messages: {
    key?: string; message: OverheadMessage; element: HTMLElement; started: number; duration: number; lane: number;
    x: number; y: number; pop: number; pulse: number; opacity: number; halfWidth: number; halfHeight: number; visible: boolean; width?: number; height?: number;
  }[] = [];
  private point = new THREE.Vector3();
  private view = new THREE.Vector3();
  private placed: { left: number; right: number; top: number; bottom: number }[] = [];
  private positions: ReadonlyMap<string, THREE.Object3D> = new Map();
  private hasFrame = false;
  private zoom = 1;
  private level: OverheadTextLevel = 'all';
  private reducedMotion = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
  constructor(private host: HTMLElement, private heights: ReadonlyMap<string, number> = new Map()) {}

  /** How much text shows (the player's setting): numbers are damage, healing and misses; notices are ability and condition captions. */
  setLevel(level: OverheadTextLevel) {
    if (level === this.level) return;
    this.level = level;
    this.messages = this.messages.filter(item => this.allows(item.message) || (item.element.remove(), false));
  }
  private allows(message: OverheadMessage) { return this.level === 'all' || this.level === 'effects' && message.style === 'notice'; }

  show(message: OverheadMessage, now = performance.now()) { this.add(message, now); }
  /** Holds a caption over its unit while a state lasts (until released with `undefined`); the same key again keeps it in place. */
  sustain(key: string, message: OverheadMessage | undefined, now = performance.now()) {
    const index = this.messages.findIndex(item => item.key === key);
    if (!message) { if (index >= 0) this.messages.splice(index, 1)[0].element.remove(); return; }
    if (index < 0) this.add({ ...message, duration: Infinity }, now, key);
  }
  private add(message: OverheadMessage, now: number, key?: string) {
    if (!message.text || !this.allows(message)) return;
    const duration = message.duration ?? 1400;
    if (!(key ? duration === Infinity : Number.isFinite(duration)) || duration <= 0) return;
    // Retain occupied lanes until their messages expire, so later arrivals don't move older text.
    this.expire(now);
    const occupied = new Set(this.messages.filter(({ message: other }) => message.unitId
      ? other.unitId === message.unitId
      : !other.unitId && other.point.x === message.point.x && other.point.z === message.point.z).map(item => item.lane));
    let lane = 0; while (occupied.has(lane)) lane++;
    const element = document.createElement('div');
    element.className = `overhead-message overhead-message--${message.style ?? 'notice'}${message.flourish ? ` overhead-message--${message.flourish}` : ''}`;
    if (message.flourish) {
      // A flourish animates its own scale and turn inside the placed element: on the element itself they would scale its screen position too.
      // Its layers (blood running down the letters) repeat the text from `data-text`.
      const inner = document.createElement('span'); inner.className = 'overhead-message__flourish';
      inner.textContent = message.text; inner.setAttribute?.('data-text', message.text);
      // A bare test double has no `append`: it still carries the text.
      if (element.append) element.append(inner); else element.textContent = message.text;
    } else element.textContent = message.text;
    element.style.color = `#${message.color.toString(16).padStart(6, '0')}`;
    element.hidden = true;
    this.host.append(element);
    this.messages.push({
      key, message: { ...message, point: { ...message.point }, effects: [...(message.effects ?? [])] },
      element, started: now, duration, lane, x: 0, y: 0, pop: 1, pulse: 1, opacity: 1, halfWidth: 0, halfHeight: 0, visible: false,
    });
    // Bound presentation memory even if rendering is suspended while events arrive.
    if (this.messages.length > 64) this.messages.shift()!.element.remove();
  }

  updateFrame(positions: ReadonlyMap<string, THREE.Object3D>) { this.positions = positions; this.hasFrame = true; }
  clear() {
    for (const item of this.messages) item.element.remove();
    this.messages = []; this.positions = new Map(); this.hasFrame = false;
  }
  private expire(now: number) {
    let write = 0;
    for (const item of this.messages) {
      if (now - item.started >= item.duration) { item.element.remove(); continue; }
      this.messages[write++] = item;
    }
    this.messages.length = write;
  }
  render(camera: THREE.Camera, width = this.host.clientWidth, height = this.host.clientHeight, now = performance.now(), reducedMotion = this.reducedMotion?.matches ?? false) {
    this.expire(now);
    if (!this.messages.length) return;
    for (const item of this.messages) {
      const { message, element, started, duration, lane } = item;
      const age = now - started, progress = Math.max(0, age / duration);
      const figure = message.unitId ? this.positions.get(message.unitId) : undefined;
      let visible = !message.unitId || !this.hasFrame || !!figure;
      for (let node = figure; node; node = node.parent ?? undefined) if (!node.visible) visible = false;
      const point = this.point.set(message.point.x, message.point.y, message.point.z);
      if (figure) {
        figure.getWorldPosition(point);
        point.y += (this.heights.get(message.unitId!) ?? 2) + (message.clearance ?? .3);
      }
      const effects = message.effects;
      if (!reducedMotion && effects?.includes('rise')) point.y += progress * .7;
      const view = this.view.copy(point).applyMatrix4(camera.matrixWorldInverse);
      point.project(camera);
      item.visible = visible && view.z < 0 && point.z >= -1 && point.z <= 1 && Math.abs(point.x) <= 1.1 && Math.abs(point.y) <= 1.1;
      if (element.hidden === item.visible) element.hidden = !item.visible;
      const pop = item.pop = !reducedMotion && effects?.includes('pop') ? 1 + .2 * Math.sin(Math.min(1, age / 240) * Math.PI) : 1;
      const pulse = item.pulse = !reducedMotion && effects?.includes('pulse') ? 1 + .04 * Math.sin(age / 120) : 1;
      const shake = !reducedMotion && effects?.includes('shake') ? Math.sin(age / 22) * 4 * Math.max(0, 1 - age / 350) : 0;
      item.x = (point.x + 1) / 2 * width + shake;
      item.y = (1 - point.y) / 2 * height;
      item.opacity = Math.min(1, (duration - age) / Math.min(400, duration));
    }

    // A caption's size and the interface zoom are read once, when it first shows: reading them every frame forced a layout after the
    // previous frame's writes, a millisecond or more in a fight. The transform's scale is applied on top, as before.
    const unmeasured = this.messages.some(item => item.visible && item.width === undefined);
    // The zoom is the root's inline custom property (ui-scale.ts): reading the inline style forces no style recalculation, as getComputedStyle would.
    if (unmeasured) this.zoom = Number.parseFloat(this.host.ownerDocument?.documentElement.style.getPropertyValue('--ui-zoom') ?? '') || 1;
    const zoom = this.zoom;
    for (const item of this.messages) {
      item.y -= item.lane * 26 * zoom;
      if (!item.visible) continue;
      const { element, message, pop, pulse } = item;
      if (item.width === undefined) {
        // Captions repeat (the same damage, the same notice): each look and text is measured once, as reading a size forces a layout.
        const key = `${element.className}|${message.text}|${zoom}`, known = measured.get(key);
        if (known) [item.width, item.height] = known;
        else {
          item.width = element.offsetWidth || message.text.length * 8 * zoom; item.height = element.offsetHeight || 20 * zoom;
          if (element.offsetWidth) { if (measured.size > 512) measured.clear(); measured.set(key, [item.width, item.height]); }
        }
      }
      item.halfWidth = item.width * pop * pulse / 2;
      item.halfHeight = item.height! * pop * pulse / 2;
    }

    let placedCount = 0;
    for (const item of this.messages) {
      const { element, x, pop, pulse, duration, opacity } = item;
      let { y } = item;
      if (item.visible) {
        const { halfWidth, halfHeight } = item;
        // Nearby units can share the same screen area too, especially at initiative.
        for (let i = 0; i < placedCount; i++) {
          const overlap = this.placed[i];
          if (x + halfWidth > overlap.left && x - halfWidth < overlap.right && y + halfHeight + 4 > overlap.top && y - halfHeight - 4 < overlap.bottom) {
            y = overlap.top - halfHeight - 4; break;
          }
        }
        const placement = this.placed[placedCount] ?? { left: 0, right: 0, top: 0, bottom: 0 };
        placement.left = x - halfWidth; placement.right = x + halfWidth;
        placement.top = y - halfHeight; placement.bottom = y + halfHeight;
        this.placed[placedCount++] = placement;
      }
      item.y = y;
      element.style.transform = `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${pop * pulse})`;
      element.style.opacity = String(opacity);
    }
  }
}
